// Modal for creating a new scene via the Gemini generator. The admin types a
// slug + label/title/alt + prompt, clicks Generate, reviews the image, and
// either regenerates or clicks Done. Each generate call hits
// POST /api/admin/scenes/generate; the scene row exists (with image) after
// the very first success — "Done" just closes the modal.

import { useCallback, useState } from "react";
import { invalidateScenes, type Scene } from "@/lib/scenes";

type Props = {
  existingSlugs: Set<string>;
  onClose: () => void;
};

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export default function NewSceneModal({ existingSlugs, onClose }: Props) {
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [title, setTitle] = useState("");
  const [alt, setAlt] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The scene returned by the server after a successful generate. Presence
  // of this toggles the modal from "form" mode into "review/regenerate" mode.
  const [generated, setGenerated] = useState<Scene | null>(null);

  const slugTaken = slug.length > 0 && existingSlugs.has(slug) && generated?.slug !== slug;
  const slugInvalid = slug.length > 0 && !SLUG_RE.test(slug);
  const canGenerate =
    !generating &&
    prompt.trim().length > 0 &&
    (generated
      ? true
      : slug.length > 0 && !slugInvalid && !slugTaken && label.trim().length > 0 && title.trim().length > 0);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/scenes/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          slug,
          label: label.trim(),
          title: title.trim(),
          alt: alt.trim(),
          prompt: prompt.trim(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? `Request failed (${res.status})`);
        return;
      }
      const body = (await res.json()) as { scene: Scene };
      setGenerated(body.scene);
      // Refresh the scenes list everywhere so the dashboard picks up the new
      // or updated scene immediately.
      invalidateScenes();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      setGenerating(false);
    }
  }, [slug, label, title, alt, prompt]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new scene"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          color: "var(--text)",
          maxWidth: 720, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
          padding: 24,
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column", gap: 16,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.2rem" }}>
              {generated ? "Regenerate or finish" : "New scene"}
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "0.88rem" }}>
              Describe the scene. Gemini will generate it. Regenerate until happy, then click Done.
            </p>
          </div>
          <button type="button" onClick={onClose} className="secondary-button"
            style={{ padding: "4px 10px", fontSize: "0.85rem" }}>
            Close
          </button>
        </div>

        {generated && (
          <div>
            <p style={{ margin: "0 0 8px 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
              Current image ({generated.width}×{generated.height}):
            </p>
            <img
              src={generated.image}
              alt={generated.alt}
              style={{ width: "100%", display: "block", background: "#000" }}
            />
          </div>
        )}

        <Field label="Slug" hint="Lowercase letters, digits, _ or - (e.g. sushi_bar)">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={generating || Boolean(generated)}
            placeholder="sushi_bar"
            style={inputStyle}
          />
          {slugInvalid && <FieldError>Must be lowercase letters, digits, _ or -.</FieldError>}
          {slugTaken && <FieldError>A scene with this slug already exists.</FieldError>}
        </Field>

        <Field label="Label" hint="Short name shown on scene cards (e.g. Sushi bar)">
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            disabled={generating} placeholder="Sushi bar" style={inputStyle} />
        </Field>

        <Field label="Title" hint="Used in the lesson header (e.g. Sushi bar lesson)">
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            disabled={generating} placeholder="Sushi bar lesson" style={inputStyle} />
        </Field>

        <Field label="Alt text" hint="Accessibility description of the scene" optional>
          <input type="text" value={alt} onChange={(e) => setAlt(e.target.value)}
            disabled={generating} placeholder="a cartoon sushi bar with a chef behind the counter"
            style={inputStyle} />
        </Field>

        <Field label="Prompt" hint="The scene itself — as short as 'sushi bar' is fine. Style, cartoon look, and 'no text / no app chrome' are added automatically.">
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
            disabled={generating}
            placeholder="sushi bar"
            rows={4}
            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
        </Field>

        {error && <FieldError>{error}</FieldError>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          {generated ? (
            <>
              <button type="button" onClick={handleGenerate} className="secondary-button"
                disabled={!canGenerate}>
                {generating ? "Regenerating..." : "Regenerate"}
              </button>
              <button type="button" onClick={onClose} className="primary-button">
                Done
              </button>
            </>
          ) : (
            <button type="button" onClick={handleGenerate} className="primary-button"
              disabled={!canGenerate}>
              {generating ? "Generating..." : "Generate"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label, hint, optional, children,
}: {
  label: string; hint?: string; optional?: boolean; children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>
        {label}{optional ? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> (optional)</span> : null}
      </span>
      {hint && <span style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>{hint}</span>}
      {children}
    </label>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <span role="alert" style={{ fontSize: "0.8rem", color: "var(--error, #e53e3e)" }}>
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  border: "1px solid var(--border, #ccc)",
  borderRadius: 0,
  background: "transparent",
  color: "inherit",
  fontSize: "0.92rem",
};
